import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MapPin, MessageCircle, Quote, MessagesSquare } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { getSaved, toggleSaved } from "@/lib/api";
import { useContact } from "@/lib/contact";
import { Avatar, Photo, VouchPill, VerifiedCheck, TypeBadge, ExampleBadge, BusinessBadge, FeaturedBadge, usePriceText, useAgo } from "./ui";

export const SaveHeart = ({ id, className = "", light }) => {
  const { t } = useApp();
  const [saved, setSaved] = useState(() => getSaved().includes(id));
  const [pop, setPop] = useState(false);
  const click = (e) => { e.preventDefault(); e.stopPropagation(); setSaved(toggleSaved(id)); setPop(true); setTimeout(() => setPop(false), 300); };
  return (
    <button data-testid={`save-heart-${id}`} aria-label={t("save")} aria-pressed={saved} onClick={click}
      className={`press w-11 h-11 rounded-full flex items-center justify-center ${light ? "bg-[#FFFDF9]/90 backdrop-blur" : "bg-[#F3EDE4]"} ${className}`}>
      <Heart className={`w-5 h-5 ${pop ? "heart-pop" : ""} ${saved ? "fill-[#C2410C] text-[#C2410C]" : "text-[#291E18]"}`} />
    </button>
  );
};

const Ribbon = ({ status }) => {
  const { t } = useApp();
  if (!["sold", "found"].includes(status)) return null;
  return <div className="ribbon bg-[#291E18] text-[#FDE68A] text-xs font-extrabold tracking-widest py-1.5 shadow-lg">{t(status).toUpperCase()}</div>;
};

const Location = ({ p }) => {
  const { L, hubOf, areaOf } = useApp();
  const area = areaOf(p.area_id);
  return (
    <div className="flex items-center gap-1 text-sm text-[#6B5E55] min-w-0">
      <MapPin className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate">{L(hubOf(p.hub_id))}{area ? ` · ${L(area)}` : ""}</span>
    </div>
  );
};

const QuestionPill = ({ n, tone = "green" }) => {
  const { t } = useApp();
  if (!n) return null;
  return (
    <span data-testid="card-question-count" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${tone === "green" ? "bg-[#ECF3EF] text-[#133E2F]" : "bg-[#FFFDF9] text-[#9A3412] ring-1 ring-[#C2410C]/30"}`}>
      <MessagesSquare className="w-3.5 h-3.5" aria-hidden="true" />{n === 1 ? t("q_count_1") : t("q_count_n", { n })}
    </span>
  );
};

const PosterRow = ({ poster }) => {
  const { t } = useApp();
  if (!poster) return null;
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Avatar user={poster} size={30} />
      <span className="text-sm font-semibold truncate">{poster.name}</span>
      {poster.phone_verified && <VerifiedCheck label={t("verified_phone")} />}
      {poster.vouch_count > 0 && <VouchPill n={poster.vouch_count} />}
    </div>
  );
};

export const ListingCard = ({ p, preview }) => {
  const { cat, L } = useApp();
  const price = usePriceText()(p);
  const ago = useAgo();
  const closed = ["sold", "found"].includes(p.status);
  const c = cat(p.category_id);
  return (
    <Link to={preview ? "#" : `/post/${p.id}`} onClick={(e) => preview && e.preventDefault()} data-testid={`listing-card-${p.id}`}
      className={`press group block rounded-[20px] bg-[#FFFDF9] card-shadow overflow-hidden relative border border-[#E8E2D9] ${closed ? "opacity-60 saturate-50" : ""}`}>
      <div className="relative">
        <Photo src={p.photos?.[0]} alt={p.title} icon={c?.icon} className="aspect-[4/3] w-full" />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5"><TypeBadge type="listing" /><FeaturedBadge post={p} />{(p.is_demo || p.poster?.is_demo) && <ExampleBadge />}</div>
        {!preview && <SaveHeart id={p.id} light className="absolute bottom-3 right-3" />}
        <Ribbon status={p.status} />
      </div>
      <div className="p-4 space-y-2">
        {price && <div data-testid="card-price" className="text-xl font-extrabold text-[#92400E]">{price}</div>}
        <h3 className="font-bold leading-snug line-clamp-2 text-[17px]">{p.title}</h3>
        <div className="flex items-center justify-between gap-2"><Location p={p} /><span className="text-xs text-[#6B5E55] shrink-0">{ago(p.created_at)}</span></div>
        <div className="flex items-center justify-between gap-2"><span className="text-xs font-semibold text-[#C2410C]">{L(c)}</span><QuestionPill n={p.question_count} /></div>
        <div className="pt-2 border-t border-[#E8E2D9] flex items-center justify-between gap-2">
          <PosterRow poster={p.poster} />
          <BusinessBadge business={p.poster?.business} />
        </div>
      </div>
    </Link>
  );
};

export const RequestCard = ({ p, preview }) => {
  const { t, cat, L } = useApp();
  const price = usePriceText()(p);
  const ago = useAgo();
  const contact = useContact();
  const closed = p.status === "found";
  return (
    <Link to={preview ? "#" : `/post/${p.id}`} onClick={(e) => preview && e.preventDefault()} data-testid={`request-card-${p.id}`}
      className={`press block rounded-[20px] bg-[#FFF4EA] border-2 border-[#C2410C]/25 card-shadow overflow-hidden relative ${closed ? "opacity-60 saturate-50" : ""}`}>
      <div className="h-1.5 bg-[#C2410C]" aria-hidden="true" />
      <Ribbon status={p.status} />
      <div className="p-5 space-y-3">
        <div className="flex items-center gap-1.5 flex-wrap"><TypeBadge type="request" /><FeaturedBadge post={p} />{(p.is_demo || p.poster?.is_demo) && <ExampleBadge />}
          <span className="text-xs font-semibold text-[#9A3412]">{L(cat(p.category_id))}</span><QuestionPill n={p.question_count} tone="terracotta" /></div>
        <div className="relative pl-9">
          <Quote className="absolute left-0 top-0 w-7 h-7 text-[#C2410C] fill-[#C2410C]/15" aria-hidden="true" />
          <h3 className="text-xl font-extrabold leading-tight line-clamp-3 text-[#291E18]">{p.title}</h3>
        </div>
        <div className="flex items-center justify-between gap-2"><Location p={p} /><span className="text-xs text-[#6B5E55] shrink-0">{ago(p.created_at)}</span></div>
        {price && <div className="inline-flex rounded-full bg-[#FFFDF9] px-3 py-1 text-sm font-extrabold text-[#92400E] ring-1 ring-[#F59E0B]/60">{price}</div>}
        <div className="flex items-center justify-between gap-2"><PosterRow poster={p.poster} /><BusinessBadge business={p.poster?.business} /></div>
        {!closed && (
          <div className="pt-1">
            <div className="text-sm font-bold text-[#9A3412] mb-2">{t("can_you_help")}</div>
            <button data-testid={`request-card-whatsapp-${p.id}`} disabled={preview}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!preview) contact(p); }}
              className="press w-full min-h-[48px] rounded-full bg-[#128C7E] hover:bg-[#0f7a6d] text-white font-bold flex items-center justify-center gap-2">
              <MessageCircle className="w-5 h-5" aria-hidden="true" /> {t("contact_whatsapp")}
            </button>
          </div>
        )}
      </div>
    </Link>
  );
};

export const PostCard = ({ p, preview }) => (p.type === "request" ? <RequestCard p={p} preview={preview} /> : <ListingCard p={p} preview={preview} />);

export const SkeletonCard = () => (
  <div className="rounded-[20px] bg-[#FFFDF9] border border-[#E8E2D9] overflow-hidden" data-testid="skeleton-card">
    <div className="skeleton aspect-[4/3]" />
    <div className="p-4 space-y-3"><div className="skeleton h-5 w-1/3 rounded-full" /><div className="skeleton h-4 w-5/6 rounded-full" /><div className="skeleton h-4 w-2/3 rounded-full" /></div>
  </div>
);
